import { instance } from '@/api/config';
import { IPatchAgreementsBodyDto } from '@/types/agreements';
import { IAppsPatchDto } from '@/types/apps';
import { IPatchCallForwardingBodyDto } from '@/types/call-forwarding';
import { UpdateChatMessageRequest } from '@/types/chat-messages-list';
import { ICityPatchBodyDto, IPatchSubCityBodyDto } from '@/types/city';
import { ICompanyPatchDto } from '@/types/companies';
import { IContainersPatchDto } from '@/types/containers';
import { ICounteryPatchBodyDto } from '@/types/country';
import { IPatchDamagePackageBodyDto } from '@/types/damage-package';
import { UpdateDistrictDto } from '@/types/districts';
import { IDocumentPatchDto } from '@/types/document';
import { IDoorPatchDto } from '@/types/door';
import { ILossPublicUploadConfirmed, IPatchLostPackageBodyDto, IRespondDocumentRequestBodyDto } from '@/types/lost-packages';
import { IMissionPatchDto } from '@/types/mission';
import { IPackTypesPatchBodyDto } from '@/types/pack-types';
import { IPatchPackageBodyDto } from '@/types/package';
import { IPatchPackageRequestBodyDto } from '@/types/package-request';
import { IPersonelPatchDto } from '@/types/personel';
import { IPatchPointBodyDto, IPatchPointMapBodyDto } from '@/types/point';
import { IRegionContactDetailPatchDto } from '@/types/region-contact-detail';
import { IPatchRegionDetailBodyDto } from '@/types/region-detail';
import { RegionTransferTypesUpdate } from '@/types/region-transfer-types';
import { IRouteCachePatchDto } from '@/types/route-cache';
import { IPatchRouteBodyDto, IPatchRouteSessionsBodyDto } from '@/types/route-sessions';
import { IPatchShipmentRouteBodyDto } from '@/types/shipment-route';
import { ISupplierPatchBodyDto } from '@/types/suppliers';
import { TransferVehicleEntryUpdate } from '@/types/transfer-vehicle-entry';
import { IPatchUserBodyDto, IPatchUserStateBodyDto } from '@/types/user';
import { IPatchUserCompanyPermissionsBodyDto } from '@/types/user-company-permissions';
import { IPatchUserInsurancePermissionsBodyDto } from '@/types/user-insurance-permissions';
import { IPatchUserOperationsPermissionsBodyDto } from '@/types/user-operations-permissions';
import { IPatchUserVendorPermissionsBodyDto } from '@/types/user-vendor-permissions';
import { IPatchUserXDockPermissionsBodyDto } from '@/types/user-xdock-permission';
import { IVehiclePlanPatchBodyDto } from '@/types/vehicle-plan';
import { IPatchVehicleRequestBodyDto } from '@/types/vehicle-request';
import { IVehicleCategoryPatchBodyDto, IVehiclePatchBodyDto } from '@/types/vehicles';
import { IPatchVendorBoyDto } from '@/types/vendor';
import { IPatchWebhooksBodyDto } from '@/types/webhooks';

export const patchCareerForm = async (id: string, body: { notes: string[] }) => {
  return await instance(`career-form/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchPackage = async (id: number | string, body: IPatchPackageBodyDto) => {
  return await instance(`package/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchDamagePackage = async (id: number | string, body: IPatchDamagePackageBodyDto) => {
  return await instance(`damagePackage/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const patchInventory = async (id: number | string, body: any) =>
  instance(`inventory/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

export const patchFileUploadRouteSessions = async (id: number | string, uploadId: number | string) => {
  return await instance(`routeSessions/${id}/upload/${uploadId}`, {
    method: 'PATCH'
  });
};

/** Hesapsız belge yükleme: S3 PUT sonrası onay (token'lı) */
export const patchPublicLossDocumentUploadConfirm = async (token: string, uploadId: number): Promise<ILossPublicUploadConfirmed> => {
  return await instance(`lostPackages/public/document-requests/${encodeURIComponent(token)}/uploads/${uploadId}`, {
    method: 'PATCH'
  });
};

export const patchFileUploadLostPackages = async (id: number | string, uploadId: number | string) => {
  return await instance(`lostPackages/${id}/upload/${uploadId}`, {
    method: 'PATCH'
  });
};

export const patchRouteSessions = async (id: number | string | undefined, body: IPatchRouteSessionsBodyDto) => {
  return await instance(`routeSessions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchRouteSessionsNextDayTransferPackage = async (id: number | string | undefined, body: IPatchRouteSessionsBodyDto) => {
  return await instance(`routeSessions/${id}/nextdaytransferpackage`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchShipmentRoute = async (id: number | string, body: IPatchShipmentRouteBodyDto) => {
  return await instance(`shipmentRoute/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchVendor = async (id: number | string | undefined, body: IPatchVendorBoyDto) => {
  return await instance(`vendor/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchPoint = async (id: number | string | undefined, body: IPatchPointBodyDto) => {
  return await instance(`point/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchPointMap = async (id: number | string | undefined, body: IPatchPointMapBodyDto) => {
  return await instance(`pointMap/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchCountery = async (id: number | string | undefined, body: ICounteryPatchBodyDto) => {
  return await instance(`countery/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchCity = async (id: number | string | undefined, body: ICityPatchBodyDto) => {
  return await instance(`city/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchSubCity = async (id: number | string | undefined, body: IPatchSubCityBodyDto) => {
  return await instance(`subCity/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchPackTypes = async (id: number | string | undefined, body: IPackTypesPatchBodyDto) => {
  return await instance(`packType/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchVehicle = async (id: number | string | undefined, body: IVehiclePatchBodyDto) => {
  return await instance(`vehicles/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchVehicleCategory = async (id: number | string | undefined, body: IVehicleCategoryPatchBodyDto) => {
  return await instance(`vehicles/categories/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchRoute = async (id: number | string | undefined, body: IPatchRouteBodyDto) => {
  return await instance(`route/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchVehiclePlan = async (id: string | undefined, body: IVehiclePlanPatchBodyDto) => {
  return await instance(`vehiclePlan/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchVehicleRequest = async (id: number | string | undefined, body: IPatchVehicleRequestBodyDto) => {
  return await instance(`requestVehicle/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const approveVehicleRequest = async (id: string) => {
  return await instance(`requestVehicle/${id}/approve`, {
    method: 'PATCH'
  });
};

export const cancelVehicleRequest = async (id: string, cancelReason: string) => {
  return await instance(`requestVehicle/${id}/cancel`, {
    method: 'PATCH',
    body: JSON.stringify({ cancelReason })
  });
};

export const postponeVehicleRequest = async (id: string, postponedDate: string) => {
  return await instance(`requestVehicle/${id}/postpone`, {
    method: 'PATCH',
    body: JSON.stringify({ postponedDate })
  });
};

export const planVehicleRequest = async (id: string, vehiclePlanId: string) => {
  return await instance(`requestVehicle/${id}/plan`, {
    method: 'PATCH',
    body: JSON.stringify({ vehiclePlanId })
  });
};

export const redirectVehicleRequest = async (id: string) => {
  return await instance(`requestVehicle/${id}/redirect`, {
    method: 'PATCH'
  });
};

// ── Paket Alım Talebi ──
export const patchPackageRequest = async (id: number | string | undefined, body: IPatchPackageRequestBodyDto) => {
  return await instance(`package-request/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchSupplier = async (id: string | undefined, body: ISupplierPatchBodyDto) => {
  return await instance(`supplier/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchContainers = async (id: string | undefined, body: IContainersPatchDto) => {
  return await instance(`containers/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchMission = async (id: string | undefined, body: IMissionPatchDto) => {
  return await instance(`mission/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchDoor = async (id: string | undefined, body: IDoorPatchDto) => {
  return await instance(`gate/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchPersonel = async (id: string | undefined, body: IPersonelPatchDto) => {
  return await instance(`personel/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchLostPackage = async (id: string | undefined, body: IPatchLostPackageBodyDto) => {
  return await instance(`lostPackages/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchApps = async (id: string | undefined, body: IAppsPatchDto) => {
  return await instance(`apps/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchUserCompanyPermissions = async (id: number | undefined, body: IPatchUserCompanyPermissionsBodyDto) => {
  return await instance(`userCompanyPermissions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchUserXDockPermissions = async (id: number | undefined, body: IPatchUserXDockPermissionsBodyDto) => {
  return await instance(`userXdockPermissions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchUserVendorPermissions = async (id: number | undefined, body: IPatchUserVendorPermissionsBodyDto) => {
  return await instance(`userVendorPermissions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchUserInsurancePermissions = async (id: number | undefined, body: IPatchUserInsurancePermissionsBodyDto) => {
  return await instance(`userInsurencePermissions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchUserOperationPermissions = async (id: number | undefined, body: IPatchUserOperationsPermissionsBodyDto) => {
  return await instance(`userOperationPermissions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchDocument = async (id: string | undefined, body: IDocumentPatchDto) => {
  return await instance(`document/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchFileUploadDocument = async (id: number | string, uploadId: number | string) => {
  return await instance(`document/${id}/upload/${uploadId}`, {
    method: 'PATCH'
  });
};

/** Kullanıcıyı pasife al / aktif et — yalnız SUPERADMIN (PATCH /user/:id/state) */
export const patchUserState = async (id: number, body: IPatchUserStateBodyDto) => {
  return await instance(`user/${id}/state`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchUser = async (id: number | undefined, body: IPatchUserBodyDto) => {
  return await instance(`user/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchWebhooks = async (id: string | undefined, body: IPatchWebhooksBodyDto) => {
  return await instance(`webhooks/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchRegionDetail = async (id: string | undefined, body: IPatchRegionDetailBodyDto) => {
  return await instance(`regionDetail/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchRegionContactDetail = async (id: number | undefined, body: IRegionContactDetailPatchDto) => {
  return await instance(`regionContactDetail/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchRouteCache = async (id: number | string | undefined, body: IRouteCachePatchDto) => {
  return await instance(`route-cache/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchCompany = async (id: number | undefined, body: ICompanyPatchDto) => {
  return await instance(`company/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchCallForwarding = async (id: string | undefined, body: IPatchCallForwardingBodyDto) => {
  return await instance(`call-forwarding/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchAgreements = async (id: string | undefined, body: IPatchAgreementsBodyDto) => {
  return await instance(`agreements/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchRegionTransferTypes = async (id: string | undefined, body: RegionTransferTypesUpdate) => {
  return await instance(`region-transfer-types/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchTransferVehicleEntry = async (id: number | string | undefined, body: TransferVehicleEntryUpdate) => {
  return await instance(`transfer-vehicle-entry/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchChatMessagesList = async (id: number | string | undefined, body: UpdateChatMessageRequest) => {
  return await instance(`chat-messages-list/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchNextDayTransferPackage = async (id: number | string) => {
  return await instance(`package/${id}/nextdaytransferpackage`, {
    method: 'PATCH'
  });
};

export const patchDistrict = async (id: number | string | undefined, body: UpdateDistrictDto) => {
  return await instance(`districts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

export const patchRespondDocumentRequest = async (id: string, reqId: string, body: IRespondDocumentRequestBodyDto) => {
  return await instance(`lostPackages/${id}/document-requests/${reqId}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};
